<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_COMPLIANCE_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <template #cell(lastActivityDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchBVNNINComplianceReport"
      @cancel="resetFilters"
    >
      <validation-observer ref="bvnNINComplianceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
          <!-- Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- ID Type -->
            <b-col md="6">
              <SelectField
                id="id-type"
                v-model="filter.idType"
                :reduce="option => option.value"
                :label="$t('ID Type')"
                placeholder="Select"
                :options="translatedIdTypeOptions"
                item="label"
              />
            </b-col>
            <!-- ID Type -->

            <!-- Account Tier Level -->
            <b-col md="6">
              <SelectField
                id="account-tier-level"
                v-model="filter.accountTierLevel"
                :reduce="option => option.value"
                :label="$t('Account Tier Level')"
                :placeholder="$t('Select')"
                :options="accountTierOptions"
                item="label"
              />
            </b-col>
            <!-- Account Tier Level -->
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product-name"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
          <!-- Branch -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BBadge, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails, accountTier } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BBadge,
    BDropdown,
    BDropdownItem,
    TableEntries,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabled: false,
      query: '',
      getStatusColor,
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('BVN/NIN Compliance Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'bvn', sortable: true, label: 'BVN ID' },
        { key: 'nin', sortable: true, label: 'NIN ID' },
        { key: 'accountTierLevel', sortable: true, label: 'Account Tier Level' },
        { key: 'accountOfficerName', sortable: true, label: 'Account Officer' },
        { key: 'accountStatus', sortable: true, label: 'Status' },
        { key: 'lastActivityDate', sortable: true, label: 'Last Activity Date' },
      ],
      filter: {
        accountName: '',
        accountNumber: '',
        idType: '',
        accountTierLevel: '',
        productCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      idTypeOptions: [
        { label: 'BVN', value: 'BVN' },
        { label: 'NIN', value: 'NIN' },
      ],
      // accountTierOptions: accountTier,
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Customer ID': item.customerID,
        'Account Name': item.accountName,
        'Account Number': item.accountNumber,
        'Account Type': item.accountType,
        'Customer Type': item.customerType,
        Product: item.productName,
        Branch: item.branchName,
        'BVN ID': item.bvn,
        'NIN ID': item.nin,
        'Account Tier Level': item.accountTierLevel,
        'Account Officer': item.accountOfficerName,
        Status: item.accountStatus,
        'Last Activity Date': item.lastActivityDate ? this.formatCustomDate(item.lastActivityDate) : null,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedIdTypeOptions() {
      return this.idTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    accountTierOptions() {
      return accountTier.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBVNNINComplianceReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBVNNINComplianceReport()
    },
  },
  mounted() {
    this.fetchBVNNINComplianceReport()
    this.fetchProduct()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranchByCode: 'operations/getBranchByCode',
      getBVNNINComplianceReport: 'reports/getBVNNINComplianceReport',
      downloadComplianceReports: 'reports/downloadComplianceReports',
    }),
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchBVNNINComplianceReport()
    },
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
        this.filter.financialDate = null
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: { status: 'Active' },
          page: 0,
          limit: 0,
          permission: reportPermissions.VIEW_COMPLIANCE_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async fetchBVNNINComplianceReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getBVNNINComplianceReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    // async csvExport() {
    //   try {
    //     this.isBusy = true
    //     const response = await this.downloadComplianceReports({
    //       fileExportFormat: 1,
    //       permission: reportPermissions.VIEW_COMPLIANCE_REPORT,
    //     })
    //     if (response.status === 200) {
    //       const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
    //       FileSaver.saveAs(blob, 'BVN-NIN Compliance Report.csv')
    //       this.alertSuccess({ text: 'Your request to download BVN-NIN Compliance Report was successful' })
    //     }
    //   } catch (error) {
    //     this.handleError(error)
    //   } finally {
    //     this.isBusy = false
    //   }
    // },
    // async excelExport() {
    //   try {
    //     this.isBusy = true
    //     const response = await this.downloadComplianceReports({
    //       fileExportFormat: 0,
    //       permission: reportPermissions.VIEW_COMPLIANCE_REPORT,
    //     })
    //     if (response.status === 200) {
    //       const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
    //       FileSaver.saveAs(blob, 'BVN-NIN Compliance Report.xlsx')
    //       this.alertSuccess({ text: 'Your request to download BVN-NIN Compliance Report Report was successful' })
    //     }
    //   } catch (error) {
    //     this.handleError(error)
    //   } finally {
    //     this.isBusy = false
    //   }
    // },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadComplianceReports({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_COMPLIANCE_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadComplianceReports({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_COMPLIANCE_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
